// ─────────────────────────────────────────────────────────────────────────────
// Overlay GLOBAL del detalle de un reconocimiento, disparado por el query param `?reco={id}`.
//
// Regla (definida por producto): una notificación de RECONOCIMIENTO SOLO abre el reconocimiento en su
// modal. NO navega a ninguna campaña ni al muro Brillamos — el reconocimiento se muestra correcto
// (con su etiqueta de campaña) sin importar qué campaña esté activa hoy, así que no hay "muro de
// fondo equivocado". La campana (components.tsx) solo añade `?reco=` a la ubicación ACTUAL y este
// overlay —montado en AppLayout— abre el modal encima, sin cambiar de pantalla.
//
// (La navegación "a la campaña" es de OTRO escenario: las notificaciones de CAMPAÑA, que hoy no están
// mapeadas a ese destino; cuando lo estén, navegarán solo si la campaña está activa.)
//
// Seguridad: el id llega en la propia notificación del usuario (un reconocimiento que recibió), caso
// seguro de `GET /v1/recognitions/{id}`. Borrado/moderado/404 → aviso honesto "no disponible".
// ─────────────────────────────────────────────────────────────────────────────
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecognitionDetail, useRecognitionCatalog } from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PostItDetailModal } from './PostItDetailModal';

/** Query param que abre el detalle de un reconocimiento como overlay. Debe coincidir con el que
 *  compone el backend (`RecognitionDeepLinks.RecognitionDetailParam`). */
export const RECO_DETAIL_PARAM = 'reco';

export function RecognitionDetailOverlay() {
  const { t } = useTranslation('recognition');
  const [params, setParams] = useSearchParams();
  const recoId = params.get(RECO_DETAIL_PARAM) || undefined;
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();
  // Defensa adicional al fix de identidad de useCurrentUser: no pedir el detalle hasta que el
  // perfil esté 100% resuelto (mismo gate que PulsoLayout/AdminView/Sidebar), para no alcanzar a
  // pintar el reconocimiento de OTRA persona con la cuenta MSAL todavía sin confirmar.
  const profileResolved = currentUser?.profileResolved ?? false;

  // `enabled: Boolean(id)` en el hook: sin `?reco`, o mientras el perfil no resuelve, no dispara consulta.
  const detail = useRecognitionDetail(profileResolved ? recoId : undefined);
  const catalog = useRecognitionCatalog();

  if (!recoId) return null;

  // Quita SOLO `?reco`, conservando el resto de la query (p. ej. `?scenario=…` que lee AppLayout), y
  // con `replace` para no ensuciar el historial con un paso intermedio "modal abierto".
  const close = () => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(RECO_DETAIL_PARAM);
      return next;
    }, { replace: true });
  };

  const recognition = detail.data?.recognition;
  if (recognition) {
    // `actionConfig` por omisión (DEFAULT_ACTION_CONFIG en el modal): editar/eliminar quedan OFF, que
    // es lo correcto para un detalle abierto desde una notificación; solo ver y reaccionar.
    return (
      <PostItDetailModal
        recognition={recognition}
        catalog={catalog.data}
        onClose={close}
        currentUserId={demoUser.userId}
      />
    );
  }

  // No disponible (borrado / moderado / fuera de visibilidad → 404) o error de red.
  if (detail.isError) {
    return createPortal(
      <div className="modal-overlay postit-modal-overlay" role="dialog" aria-modal="true" onClick={close}>
        <div className="postit-modal-card" onClick={(e) => e.stopPropagation()}>
          <button className="postit-modal-close" onClick={close} aria-label={t('common.close')}>
            <i className="fas fa-times" />
          </button>
          <p className="postit-modal-message">{t('notifications.recognitionUnavailable')}</p>
        </div>
      </div>,
      document.body,
    );
  }

  // Cargando: sin overlay ruidoso. Al abrir desde el muro el detalle suele venir de caché de React
  // Query; el parpadeo de un spinner sería peor que la breve espera.
  return null;
}
